import { useCallback, useRef, useState } from "react";

export default function useVideoPanel(localStreamRef) {
  const localVideoRef = useRef(null);
  const resizeStateRef = useRef({ startY: 0, startHeight: 320, active: false });
  const [videoPanelHeight, setVideoPanelHeight] = useState(320);

  const attachLocalPreview = useCallback(() => {
    const videoEl = localVideoRef.current;
    const tracks = localStreamRef.current?.getVideoTracks?.() || [];
    if (!videoEl) return;
    if (!tracks.length) {
      videoEl.srcObject = null;
      return;
    }
    const stream = new MediaStream(tracks);
    videoEl.srcObject = stream;
    videoEl.muted = true;
    videoEl.playsInline = true;
    videoEl.autoplay = true;
    videoEl.play?.().catch(() => {});
  }, [localStreamRef]);

  const clearLocalPreview = useCallback(() => {
    if (localVideoRef.current) {
      try {
        localVideoRef.current.pause?.();
      } catch {}
      localVideoRef.current.srcObject = null;
    }
  }, []);

  const startVideoResize = useCallback(
    (event) => {
      event.preventDefault();
      event.stopPropagation();
      
      // Extract Y coordinate from mouse or touch event
      const getClientY = (e) => {
        if (e.touches && e.touches.length > 0) {
          return e.touches[0].clientY;
        }
        if (e.changedTouches && e.changedTouches.length > 0) {
          return e.changedTouches[0].clientY;
        }
        return e.clientY;
      };
      
      const startY = getClientY(event);
      
      resizeStateRef.current = {
        startY,
        startHeight: videoPanelHeight,
        active: true,
      };

      const onMove = (e) => {
        if (!resizeStateRef.current.active) return;
        e.preventDefault();
        e.stopPropagation();
        
        const currentY = getClientY(e);
        const delta = currentY - resizeStateRef.current.startY;
        const next = Math.min(
          520,
          Math.max(200, resizeStateRef.current.startHeight + delta),
        );
        setVideoPanelHeight(next);
      };

      const onEnd = (e) => {
        if (e) {
          e.preventDefault();
          e.stopPropagation();
        }
        resizeStateRef.current.active = false;
        
        // Remove all event listeners
        window.removeEventListener("pointermove", onMove);
        window.removeEventListener("pointerup", onEnd);
        window.removeEventListener("pointercancel", onEnd);
        window.removeEventListener("touchmove", onMove);
        window.removeEventListener("touchend", onEnd);
        window.removeEventListener("touchcancel", onEnd);
        window.removeEventListener("mousemove", onMove);
        window.removeEventListener("mouseup", onEnd);
      };

      // Add both pointer, touch, and mouse event listeners for maximum compatibility
      window.addEventListener("pointermove", onMove, { passive: false });
      window.addEventListener("pointerup", onEnd, { passive: false });
      window.addEventListener("pointercancel", onEnd, { passive: false });
      window.addEventListener("touchmove", onMove, { passive: false });
      window.addEventListener("touchend", onEnd, { passive: false });
      window.addEventListener("touchcancel", onEnd, { passive: false });
      window.addEventListener("mousemove", onMove, { passive: false });
      window.addEventListener("mouseup", onEnd, { passive: false });
    },
    [videoPanelHeight],
  );

  return {
    localVideoRef,
    videoPanelHeight,
    startVideoResize,
    attachLocalPreview,
    clearLocalPreview,
  };
}
